/* eslint-disable replo/no-tailwind-literal-color */
import type { OpencodeEvent } from "schemas/opencode";

import * as React from "react";

import {
  useDebugMemorySnapshots,
  useDebugMemoryStatsActions,
} from "@agent-ui/store/debugMemoryStatsStore";
import {
  useDebugSandboxProcessLogs,
  useDebugSandboxProcessLogsActions,
} from "@agent-ui/store/debugSandboxProcessLogsStore";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import twMerge from "@replo/design-system/utils/twMerge";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { EventsTab } from "@v2/features/agent-debugger/tabs/Events";
import { LogsTab } from "@v2/features/agent-debugger/tabs/Logs";
import { MemoryTab } from "@v2/features/agent-debugger/tabs/Memory";
import { PublishTab } from "@v2/features/agent-debugger/tabs/Publish";
import { StateTab } from "@v2/features/agent-debugger/tabs/State";
import { FloatingPanel } from "@v2/features/debug/FloatingPanel";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { z } from "zod";

type DebugTab = "state" | "events" | "logs" | "memory" | "publish";

type ConnectionState = "connected" | "connecting" | "disconnected";

const CONNECTION_STATE_BADGE_CLASS: Record<ConnectionState, string> = {
  connected: "bg-green-100 text-green-700",
  connecting: "bg-yellow-100 text-yellow-700",
  disconnected: "bg-red-100 text-red-700",
};

const MAX_EVENTS = 1000;

const ConnectionBadge: React.FC<{ connectionState: string }> = ({
  connectionState,
}) => {
  const badgeClass =
    CONNECTION_STATE_BADGE_CLASS[connectionState as ConnectionState] ??
    "bg-red-100 text-red-700";

  return (
    <span
      className={twMerge(
        "rounded px-1.5 py-0.5 text-xs font-medium",
        badgeClass,
      )}
    >
      {connectionState}
    </span>
  );
};

const AgentDebugPanel: React.FC = () => {
  const isDebugMode = useIsDebugMode();
  const [isVisible, setIsVisible] = useLocalStorageState(
    "replo.debug.showAgentConnectionDebug",
    false,
    { schema: z.boolean() },
  );
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<DebugTab>("state");
  const [events, setEvents] = React.useState<
    { timestamp: number; event: OpencodeEvent }[]
  >([]);

  const processLogs = useDebugSandboxProcessLogs();
  const { clearLogs: clearProcessLogs } = useDebugSandboxProcessLogsActions();
  const memorySnapshots = useDebugMemorySnapshots();
  const { clearStats: clearMemoryStats } = useDebugMemoryStatsActions();

  const handleEvent = React.useCallback((event: OpencodeEvent) => {
    setEvents((prev) => {
      const next = [...prev, { timestamp: Date.now(), event }];
      if (next.length > MAX_EVENTS) {
        return next.slice(next.length - MAX_EVENTS);
      }
      return next;
    });
  }, []);

  const { state, _rawState, connectionState } = useReploAgent({
    onEvent: handleEvent,
  });

  // Note (Noah, 2026-02-18, REPL-24077): Only show the panel for superusers/debug mode,
  // and only when the debug option is enabled via cmd-k menu
  if (!isDebugMode || !isVisible) {
    return null;
  }

  return (
    <FloatingPanel
      title="Agent Debugger"
      storageKey="replo.debug.agentConnectionDebug"
      defaultSize={{ width: 560, height: 500 }}
      defaultPosition="bottom-left"
      isMinimized={isMinimized}
      onMinimize={() => setIsMinimized((prev) => !prev)}
      onClose={() => {
        setIsMinimized(false);
        setIsVisible(false);
      }}
      headerExtra={<ConnectionBadge connectionState={connectionState} />}
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as DebugTab)}
        UNSAFE_className="min-h-0 flex-1 text-xs"
      >
        <TabsList variant="outline" size="sm">
          <TabsTrigger value="state" size="sm" UNSAFE_className="text-xs">
            State
          </TabsTrigger>
          <TabsTrigger value="events" size="sm" UNSAFE_className="text-xs">
            Events ({events.length})
          </TabsTrigger>
          <TabsTrigger value="logs" size="sm" UNSAFE_className="text-xs">
            Logs ({processLogs.length})
          </TabsTrigger>
          <TabsTrigger value="memory" size="sm" UNSAFE_className="text-xs">
            Memory
          </TabsTrigger>
          <TabsTrigger value="publish" size="sm" UNSAFE_className="text-xs">
            Publish
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="state"
          UNSAFE_className="flex min-h-0 flex-1 flex-col p-3"
        >
          <StateTab state={state} />
        </TabsContent>
        <TabsContent
          value="events"
          UNSAFE_className="flex min-h-0 flex-1 flex-col p-3"
        >
          <EventsTab events={events} onClearEvents={() => setEvents([])} />
        </TabsContent>
        <TabsContent
          value="logs"
          UNSAFE_className="flex min-h-0 flex-1 flex-col p-3"
        >
          <LogsTab processLogs={processLogs} onClearLogs={clearProcessLogs} />
        </TabsContent>
        <TabsContent
          value="memory"
          UNSAFE_className="flex min-h-0 flex-1 flex-col p-3"
        >
          <MemoryTab
            snapshots={memorySnapshots}
            onClearStats={clearMemoryStats}
          />
        </TabsContent>
        <TabsContent
          value="publish"
          UNSAFE_className="flex min-h-0 flex-1 flex-col p-3"
        >
          <PublishTab realPublishStatus={_rawState.publishStatus} />
        </TabsContent>
      </Tabs>
    </FloatingPanel>
  );
};

export default AgentDebugPanel;
